import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import moment from 'moment';
import InputGroup from 'react-bootstrap/InputGroup';
import CurrencyInput from 'react-currency-input-field';
import { usdCurrencyFormat } from 'resources/functions';

interface SplitData {
    rowID: number;
    deposit: number;
    deposit_debit_account_id: string;
}

interface SplitModalProps {
    rowID: number;
    show: boolean;
    onHide: () => void;
    onSave: (rowID: number, indexNo: number, splits: SplitData[]) => void;
    originalAmount: number;
    glDebitAccounts: any[];
    creditAccountName: string;
    creditAccountId: string;
    transactionAt: string;
    description: string;
    splitData: SplitData[];
    indexNo: number;
    filterDebitAccountList: (inputValue: string) => Promise<any[]>;
}

const SplitModal: React.FC<SplitModalProps> = ({
    rowID, show, onHide, onSave, originalAmount, glDebitAccounts, creditAccountName, creditAccountId, transactionAt, description, splitData, indexNo, filterDebitAccountList
}) => {
    const { t } = useTranslation('components.Modal.ExpenseClassification.splitModal');
    const { handleSubmit, control, formState: { errors }, reset, setValue } = useForm({
        defaultValues: {
            splits: [{ amount: '', debitAccount: '' }]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'splits'
    });

    const [remainingAmount, setRemainingAmount] = useState(originalAmount);
    const amountInputRef = useRef<HTMLInputElement | null>(null);
    const amountRefs = useRef<Array<HTMLInputElement | null>>([]);
    //const amountInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const splits = useWatch({ control, name: 'splits' });

    useEffect(() => {
        const totalAmount = splits.reduce((sum, split) => {
            const amount = split.amount ? parseFloat(split.amount.replace(/,/g, '')) : 0;
            return sum + amount;
        }, 0);
        setRemainingAmount(parseFloat((originalAmount - totalAmount).toFixed(2)));
    }, [splits, originalAmount]);

    useEffect(() => {
        if (show) {
            reset({ splits: [] });

            const existingSplits = splitData.filter(data => data.rowID === rowID);
            if (existingSplits.length > 0) {
                existingSplits.forEach(data => {
                    append({
                        amount: data.deposit.toString(),
                        debitAccount: data.deposit_debit_account_id
                    });
                });
            } else {
                append({ amount: '', debitAccount: '' });
            }

            setTimeout(() => {
                if (amountRefs.current[0]) {
                    amountRefs.current[0].focus();
                }
            }, 100);
        }
    }, [show, splitData, reset, append, rowID]);

    const onSubmit = (data: any) => {
        const totalAmount = data.splits.reduce((sum: number, split: any) => {
            const amount = split.amount ? parseFloat(split.amount.replace(/,/g, '')) : 0;
            return sum + amount;
        }, 0);
        if (parseFloat(totalAmount.toFixed(2)) === parseFloat(originalAmount.toFixed(2))) {
            const formattedSplits = data.splits.map((split: any) => ({
                id: rowID,
                deposit: parseFloat(split.amount.replace(/,/g, '')),
                deposit_debit_account_id: split.debitAccount,
                deposit_credit_account_id: creditAccountId,
                rowID: rowID,
            }));
            onSave(rowID, indexNo, formattedSplits);
            reset();
            onHide();
        } else {
            toast.error(t('error.amountMismatch'));
        }
    };

    const handleClose = () => {
        reset();
        onHide();
    };
    // const handleAppend = () => {
    //     append({ amount: '', debitAccount: '' });
    //     setTimeout(() => {
    //         if (amountRefs.current[fields.length]) {
    //             amountRefs.current[fields.length].focus();
    //         }
    //     }, 100);
    // };
    const handleAppend = () => {
        append({ amount: '', debitAccount: '' });
        setTimeout(() => {
            // Check if the current reference exists before trying to focus on it
            const lastAmountInput = amountRefs.current[fields.length];
            if (lastAmountInput) {
                lastAmountInput.focus();
            }
        }, 100);
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="table-responsive">
                    <table className="table table-bordered">
                        <tbody>
                            <tr>
                                <td><b>{t('label.date')}</b></td>
                                <td>{moment(transactionAt).format('MM-DD-YYYY h:mm:ss')}</td>
                            </tr>
                            <tr>
                                <td><b>{t('label.description')}</b></td>
                                <td className="text-start text-truncate" style={{ maxWidth: '150px' }} title={description || t('label.na')}>
                                    {description || t('label.na')}
                                </td>
                            </tr>
                            <tr>
                                <td><b>{t('label.amount')}</b></td>
                                <td>{usdCurrencyFormat(originalAmount)}</td>
                            </tr>
                            <tr>
                                <td><b>{t('label.creditGLAccount')}</b></td>
                                <td>{creditAccountName}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th style={{ width: '200px' }}>{t('label.amount.column')}</th>
                                <th>{t('label.debitGLAccount')}</th>
                                <th className="text-center" style={{ width: '100px' }}>{t('label.addTransaction')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((item, index) => (
                                <tr key={item.id}>
                                    <td>
                                        <Controller
                                            name={`splits.${index}.amount`}
                                            control={control}
                                            rules={{ required: t('validation.amountRequired'), min: { value: 0, message: t('validation.amountPositive') } }}
                                            render={({ field }) => (
                                                <InputGroup hasValidation className="input-group-custom">
                                                    <InputGroup.Text>$</InputGroup.Text>
                                                    <CurrencyInput
                                                        className={`form-control ${errors.splits?.[index]?.amount ? 'is-invalid' : ''}`}
                                                        placeholder={t('placeholder.amount')}
                                                        value={field.value}
                                                        onValueChange={(value) => setValue(`splits.${index}.amount`, value || '')}
                                                        allowDecimals
                                                        decimalsLimit={2}
                                                        decimalSeparator="."
                                                        groupSeparator=","
                                                        disableAbbreviations
                                                        ref={(el) => (amountRefs.current[index] = el)}
                                                        //ref={index === 0 ? amountInputRef : null} // Set ref to first amount input field
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.splits?.[index]?.amount?.message}
                                                    </Form.Control.Feedback>
                                                </InputGroup>
                                            )}
                                        />
                                    </td>
                                    
                                    <td>
                                        <Controller
                                            name={`splits.${index}.debitAccount`}
                                            control={control}
                                            rules={{ required: t('validation.debitRequired') }}
                                            render={({ field }) => (
                                                <AsyncSelect
                                                    {...field}
                                                    loadOptions={filterDebitAccountList}
                                                    defaultOptions={glDebitAccounts}
                                                    isSearchable
                                                    placeholder={t('placeholder.selectDebitAccount')}
                                                    className={errors.splits?.[index]?.debitAccount ? 'is-invalid' : ''}
                                                />
                                            )}
                                        />
                                        <div className="invalid-feedback d-block">
                                            {errors.splits?.[index]?.debitAccount?.message}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {fields.length > 1 && (
                                            <Button variant="danger" onClick={() => remove(index)} className="me-2">
                                                <span role="img" aria-label={t('label.remove')}>-</span>
                                            </Button>
                                        )}
                                        <Button variant="secondary"  onClick={handleAppend}>
                                            <span role="img" aria-label={t('label.add')}>+</span>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    <div className="mt-3">
                        <b>{t('label.transactionATM')}</b> {usdCurrencyFormat(originalAmount)}<br />
                        <b>{t('label.remainingSplitTotal')}</b> <span style={{ color: remainingAmount < 0 ? 'red' : 'black' }}>
                        {remainingAmount < 0 ? `(${usdCurrencyFormat(Math.abs(remainingAmount))})`: usdCurrencyFormat(remainingAmount)}</span>
                    </div>
                    
                    <div className="d-flex justify-content-end mt-3">
                        <Button type="submit" variant="primary" className="me-2">{t('button.splitSubmit')}</Button>
                        <Button variant="secondary" onClick={handleClose}>{t('button.close')}</Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default SplitModal;
